/**
 * Sign Up Modal
 *
 * Modal for user registration following Figma design
 * Figma: Property 1=Sign Up (Node: 1015:30879)
 * Dimensions: 636×596px
 *
 * Features:
 * - Progressive form expansion (email → password → confirm password)
 * - OAuth buttons (Google, Apple)
 * - Progress tracking (x out of 5 steps)
 * - Design tokens for styling consistency
 */
"use client";

import React, { useEffect, useState } from "react";

import { AnimatePresence, motion } from "framer-motion";
import { Check, Eye, EyeOff, X } from "lucide-react";

import { colors, modalSpacing } from "@/styles/design-tokens";
import { isValidEmail } from "@/utils/validation-utils";

import { BasicModalShell } from "./BasicModalShell";
import { FloatingLabelInput } from "./FloatingLabelInput";
import { ModalPrimaryButton } from "./ModalPrimaryButton";
import { ModalProgressBar } from "./ModalProgressBar";
import { OAuthButton } from "./OAuthButton";
import { TextArrowButton } from "./TextArrowButton";

// All spacing now centralized in design-tokens.ts → modalSpacing

interface PasswordValidation {
  minLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
}

interface SignUpModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRegister: (email: string, password: string) => Promise<void>;
  onGoogleSignUp: () => Promise<void>;
  onAppleSignUp?: () => Promise<void>;
  onSwitchToSignIn: () => void;
  currentStep?: number;
  totalSteps?: number;
}

export function SignUpModal({
  isOpen,
  onClose,
  onRegister,
  onGoogleSignUp,
  onAppleSignUp,
  onSwitchToSignIn,
  currentStep = 1,
  totalSteps = 5,
}: SignUpModalProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [showConfirmPasswordField, setShowConfirmPasswordField] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [passwordValidation, setPasswordValidation] = useState<PasswordValidation>({
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
  });

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setError("");
      // Don't reset email/password if reopening
    } else {
      // Clear everything when modal closes
      setEmail("");
      setPassword("");
      setConfirmPassword("");
      setShowPasswordField(false);
      setShowConfirmPasswordField(false);
    }
  }, [isOpen]);

  // Show password field when user starts typing email
  useEffect(() => {
    if (email.length > 0 && !showPasswordField) {
      setShowPasswordField(true);
    } else if (email.length === 0 && showPasswordField) {
      setShowPasswordField(false);
      setShowConfirmPasswordField(false);
      setPassword("");
      setConfirmPassword("");
    }
  }, [email, showPasswordField]);

  // Show confirm password field when user starts typing password
  useEffect(() => {
    if (password.length > 0 && !showConfirmPasswordField) {
      setShowConfirmPasswordField(true);
    } else if (password.length === 0 && showConfirmPasswordField) {
      setShowConfirmPasswordField(false);
      setConfirmPassword("");
    }
  }, [password, showConfirmPasswordField]);

  // Validate password requirements
  useEffect(() => {
    setPasswordValidation({
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
    });
  }, [password]);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password || !confirmPassword) return;

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    const isValidPassword = Object.values(passwordValidation).every((valid) => valid);
    if (!isValidPassword) {
      setError("Password does not meet requirements");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      await onRegister(email, password);
      // Modal stays open, parent handles next step
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to create account. Please try again.";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setError("");
    setIsLoading(true);

    try {
      await onGoogleSignUp();
      // On desktop (popup flow), this line executes after successful auth
      // On mobile (redirect flow), the page redirects so this won't run
      setIsLoading(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to sign up with Google.";
      setError(errorMessage);
      setIsLoading(false);
    }
  };

  const handleAppleSignUp = async () => {
    if (!onAppleSignUp) return;
    setError("");
    setIsLoading(true);

    try {
      await onAppleSignUp();
      setIsLoading(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to sign up with Apple.";
      setError(errorMessage);
      setIsLoading(false);
    }
  };

  // Improved email validation
  const hasValidEmail = isValidEmail(email);
  const isValidPassword = Object.values(passwordValidation).every((valid) => valid);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;
  const canSubmit = hasValidEmail && isValidPassword && passwordsMatch;

  return (
    <BasicModalShell
      isOpen={isOpen}
      onClose={onClose}
      closeOnBackdropClick={true}
      topSlot={<ModalProgressBar currentStep={currentStep} totalSteps={totalSteps} />}
    >
      <div
        className="mx-auto flex w-full flex-col px-4 sm:px-0"
        style={{
          maxWidth: modalSpacing.signInContentWidth,
          gap: modalSpacing.sectionGap,
          paddingTop: modalSpacing.contentTopPadding,
          paddingBottom: modalSpacing.contentBottomPadding,
        }}
      >
        {/* Title */}
        <div className="text-center">
          <h2 className="text-title text-ink">Sign Up</h2>
        </div>

        {/* Error Message */}
        {error && (
          <div
            className="rounded border p-3"
            style={{
              backgroundColor: colors.feedback.errorBackground,
              borderColor: colors.feedback.errorBorder,
            }}
          >
            <p
              className="text-input-modal text-center"
              style={{
                color: colors.feedback.errorTextPrimary,
                fontSize: "12px",
              }}
            >
              {error}
            </p>
          </div>
        )}

        {/* Form */}
        <form
          onSubmit={handleRegister}
          className="flex flex-col"
          style={{ gap: modalSpacing.formFieldGap }}
        >
          <FloatingLabelInput
            type="email"
            value={email}
            onChange={setEmail}
            label="Email address"
          />

          {/* Password Input (Progressive) */}
          <AnimatePresence>
            {showPasswordField && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <FloatingLabelInput
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={setPassword}
                  label="Password"
                  rightSlot={
                    password.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="text-gray-500 transition-colors hover:text-gray-900"
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    )
                  }
                />

                {/* Password Requirements */}
                {password.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    transition={{ duration: 0.2, delay: 0.1 }}
                    className="mt-3 space-y-2"
                  >
                    <p className="mb-2 text-xs text-gray-500">Password must contain:</p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div
                        className={`flex items-center gap-2 ${passwordValidation.minLength ? "text-blue-600" : "text-gray-500"}`}
                      >
                        {passwordValidation.minLength ? (
                          <Check className="h-3 w-3" />
                        ) : (
                          <X className="h-3 w-3" />
                        )}
                        8+ characters
                      </div>
                      <div
                        className={`flex items-center gap-2 ${passwordValidation.hasUppercase ? "text-blue-600" : "text-gray-500"}`}
                      >
                        {passwordValidation.hasUppercase ? (
                          <Check className="h-3 w-3" />
                        ) : (
                          <X className="h-3 w-3" />
                        )}
                        Uppercase letter
                      </div>
                      <div
                        className={`flex items-center gap-2 ${passwordValidation.hasLowercase ? "text-blue-600" : "text-gray-500"}`}
                      >
                        {passwordValidation.hasLowercase ? (
                          <Check className="h-3 w-3" />
                        ) : (
                          <X className="h-3 w-3" />
                        )}
                        Lowercase letter
                      </div>
                      <div
                        className={`flex items-center gap-2 ${passwordValidation.hasNumber ? "text-blue-600" : "text-gray-500"}`}
                      >
                        {passwordValidation.hasNumber ? (
                          <Check className="h-3 w-3" />
                        ) : (
                          <X className="h-3 w-3" />
                        )}
                        Number
                      </div>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Confirm Password Input (Progressive) */}
          <AnimatePresence>
            {showConfirmPasswordField && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <FloatingLabelInput
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  label="Confirm password"
                  rightSlot={
                    confirmPassword.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="text-gray-500 transition-colors hover:text-gray-900"
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    )
                  }
                />

                {/* Password Match Indicator */}
                {confirmPassword.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.2 }}
                    className={`mt-2 flex items-center gap-2 text-xs ${
                      passwordsMatch ? "text-blue-600" : "text-red-500"
                    }`}
                  >
                    {passwordsMatch ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                    {passwordsMatch ? "Passwords match" : "Passwords do not match"}
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Register Button */}
          <ModalPrimaryButton type="submit" disabled={isLoading || !canSubmit}>
            {isLoading ? "Creating Account..." : "Register"}
          </ModalPrimaryButton>
        </form>

        {/* OAuth Buttons */}
        <div
          className="flex flex-col"
          style={{ gap: modalSpacing.oauthButtonGap, marginBottom: modalSpacing.sectionGap }}
        >
          <OAuthButton provider="google" onClick={handleGoogleSignUp} disabled={isLoading} />
          {onAppleSignUp && (
            <OAuthButton provider="apple" onClick={handleAppleSignUp} disabled={isLoading} />
          )}
        </div>

        {/* Sign In Section */}
        <div
          className="flex flex-col items-center border-t"
          style={{
            borderColor: colors.divider.divider1,
            paddingTop: modalSpacing.footerSectionPadding,
            paddingBottom: modalSpacing.footerSectionPadding,
            gap: modalSpacing.footerGap,
          }}
        >
          <p
            className="text-input-modal text-center"
            style={{ color: colors.text.floatingLabelInactive }}
          >
            Already have an account?
          </p>
          <TextArrowButton label="Sign In" onClick={onSwitchToSignIn} />
        </div>
      </div>
    </BasicModalShell>
  );
}
